import { createFileRoute, Link, Outlet, redirect, useRouterState, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useRef, type RefObject } from 'react'
import { cn } from '@/lib/utils'
import { useAuth, loadStoredAuth, decodeJwtPayloadUnsafe, getExpiryMs, AUTH_STORAGE_KEY } from '@/lib/auth'
import { getCircleProfileApi, getOrganizationProfileApi } from '@/lib/api'
import {
  ActivityIcon,
  BanknoteIcon,
  BellIcon,
  BookOpenIcon,
  BuildingIcon,
  CalculatorIcon,
  CheckCircleIcon,
  CreditCardIcon,
  FileTextIcon,
  FlagIcon,
  LayoutDashboardIcon,
  LifeBuoyIcon,
  LogOutIcon,
  MailIcon,
  StoreIcon,
  MenuIcon,
  PlugIcon,
  ReceiptTextIcon,
  SearchIcon,
  SendIcon,
  ShieldCheckIcon,
  SettingsIcon,
  SmartphoneIcon,
  TrendingUpIcon,
  UploadIcon,
  UserCheckIcon,
  UserCogIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import NotificationBell from '@/components/notifications/NotificationBell'

export const sidebarLinks = [
  { to: '/dashboard/pending-payments', label: 'Pending payments', icon: BanknoteIcon },
  { to: '/dashboard/notifications', label: 'Notifications', icon: BellIcon },
  { to: '/dashboard/csv-export', label: 'Generate payrun', icon: FileTextIcon },
  { to: '/dashboard/csv-import', label: 'Import payrun', icon: UploadIcon },
  { to: '/dashboard/completed-payments', label: 'Completed payments', icon: CheckCircleIcon },
  { to: '/dashboard/billing', label: 'Subscription', icon: CreditCardIcon },
  { to: '/dashboard/employees', label: 'Employees', icon: UsersIcon },
  { to: '/dashboard/add-employee', label: 'Add new employee', icon: UserPlusIcon },
  { to: '/dashboard/add-existing-user', label: 'Add existing user', icon: UserCheckIcon },
  { to: '/dashboard/notification-preferences', label: 'Notification preferences', icon: SettingsIcon },
  { to: '/dashboard/employee-settings', label: 'Employee Circle settings', icon: UserCogIcon },
  { to: '/dashboard/company-profile', label: 'Company profile', icon: BuildingIcon },
  { to: '/dashboard/payroll-defaults', label: 'Payroll defaults', icon: CalculatorIcon },
  { to: '/dashboard/integrations', label: 'Integrations', icon: PlugIcon },
  { to: '/dashboard/tax-forms', label: 'Tax data', icon: ReceiptTextIcon },
  { to: '/dashboard/help', label: 'Help & training', icon: LifeBuoyIcon },
]

const employeeLinks = [
  { to: '/dashboard', label: 'Download App', icon: SmartphoneIcon },
]

// Super-admin links — shown ONLY to super_admin users.
// Super admins see a completely separate sidebar — the standard employer
// payroll links are hidden. These bypass profile-completeness guards.
const superAdminLinks = [
  { to: '/dashboard/admin', label: 'Overview', icon: LayoutDashboardIcon },
  { to: '/dashboard/admin/org-verification', label: 'Org verification', icon: ShieldCheckIcon },
  { to: '/dashboard/admin/subscription-plans', label: 'Pricing', icon: BanknoteIcon },
  { to: '/dashboard/admin/org-subscriptions', label: 'Billing overview', icon: CreditCardIcon },
  { to: '/dashboard/admin/seller-profile', label: 'Seller profile', icon: StoreIcon },
  { to: '/dashboard/admin/revenue', label: 'Revenue', icon: TrendingUpIcon },
  { to: '/dashboard/admin/audit', label: 'Audit log', icon: SearchIcon },
  { to: '/dashboard/admin/users', label: 'Users', icon: UsersIcon },
  { to: '/dashboard/admin/user-import', label: 'User import', icon: UploadIcon },
  { to: '/dashboard/admin/organizations', label: 'Organizations', icon: BuildingIcon },
  { to: '/dashboard/admin/tax-forms', label: 'Tax supervision', icon: ReceiptTextIcon },
  { to: '/dashboard/admin/ledger', label: 'Ledger', icon: BookOpenIcon },
  { to: '/dashboard/admin/requests', label: 'Circles', icon: BanknoteIcon },
  { to: '/dashboard/admin/demo-requests', label: 'Demo requests', icon: MailIcon },
  { to: '/dashboard/admin/support-emails', label: 'Support emails', icon: MailIcon },
  { to: '/dashboard/admin/activity-feed', label: 'Activity', icon: ActivityIcon },
  { to: '/dashboard/admin/integrations', label: 'Integrations', icon: PlugIcon },
  { to: '/dashboard/admin/actions', label: 'Actions', icon: SendIcon },
  { to: '/dashboard/admin/feature-flags', label: 'Feature flags', icon: FlagIcon },
  { to: '/dashboard/admin/settings', label: 'Settings', icon: SettingsIcon },
]

// Single source of truth for sidebar markup, rendered in two places:
//   • the static desktop aside at ≥md (768px),
//   • the conditional mobile drawer below <md, opened by the hamburger.
//
// `onNavigate` is supplied only on mobile. When present, both the header's
// X button and every Link invoke it so the drawer auto-closes on tap.
// Hooks are called once at the top of this function — never inside
// event handlers — so the rules-of-hooks check stays clean.
function SidebarContent({
  currentPath,
  isEmployee,
  isSuperAdmin,
  onNavigate,
  closeBtnRef,
}: {
  currentPath: string
  isEmployee: boolean
  isSuperAdmin: boolean
  onNavigate?: () => void
  closeBtnRef?: RefObject<HTMLButtonElement | null>
}) {
  const { logout } = useAuth()
  const [loggingOut, setLoggingOut] = useState(false)

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
      // Hard reload to clear all in-memory state, HTTP cache, and module singletons.
      window.location.href = '/login'
    } catch {
      window.location.href = '/login'
    }
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="text-sm font-semibold hover:opacity-80 transition-opacity"
        >
          Dashboard
        </Link>
        {onNavigate && (
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close menu"
            onClick={onNavigate}
            className="flex size-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-5" />
          </button>
        )}
      </div>
      {/* min-h-0 is required for flex children to scroll inside a
          constrained-height flex container; without it the inner column
          would grow past its bounds and clip the logout instead of scrolling. */}
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
        {isSuperAdmin ? (
          <>
            {/* ── Super-admin sidebar header ── */}
            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
              System administration
            </p>

            {superAdminLinks.map((link) => {
              const isActive = currentPath === link.to ||
                (link.to !== '/dashboard/admin' && currentPath.startsWith(link.to))
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-l-2 border-transparent pl-[10px] pr-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground border-[var(--brand-mint)]'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <link.icon className="size-4" />
                  {link.label}
                </Link>
              )
            })}
          </>
        ) : (
          <>
            {sidebarLinks.map((link) => {
              const isActive = currentPath === link.to
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-l-2 border-transparent pl-[10px] pr-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground border-[var(--brand-mint)]'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <link.icon className="size-4" />
                  {link.label}
                </Link>
              )
            })}

            {isEmployee && (
              <>
                <div className="my-2 border-t border-border" />
                {employeeLinks.map((link) => {
                  const isActive = currentPath === link.to
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border-l-2 border-transparent pl-[10px] pr-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground border-[var(--brand-mint)]'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      <link.icon className="size-4" />
                      {link.label}
                    </Link>
                  )
                })}
              </>
            )}
          </>
        )}
        {/* mt-auto replaces the previous flex-1 spacer: it pushes the
            logout to the bottom of the nav when there's room, and degrades
            gracefully (logout scrolls with the links) when the link list
            overflows and overflow-y-auto kicks in above. */}
        <div className="mt-auto border-t border-border pt-2">
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <LogOutIcon className="size-4" />
            {loggingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </nav>
    </>
  )
}

export const Route = createFileRoute('/dashboard')({
  beforeLoad: () => {
    const stored = loadStoredAuth()
    if (!stored) {
      throw redirect({ to: '/login' })
    }
    // Also check if the stored token is expired
    const payload = decodeJwtPayloadUnsafe(stored.token)
    if (!payload || getExpiryMs(payload) <= 0) {
      localStorage.removeItem(AUTH_STORAGE_KEY)
      throw redirect({ to: '/login' })
    }
  },
  component: () => {
    const routerState = useRouterState()
    const navigate = useNavigate()
    const currentPath = routerState.location.pathname
    const { user } = useAuth()
    const isEmployee = user?.role === 'employee'
    const isSuperAdmin = user?.role === 'super_admin'
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [checkingProfile, setCheckingProfile] = useState(true)

    // ────────────────────────────────────────────────────────────────
    // Option B: Redirect guard for incomplete profiles (batched).
    // Employer-role users with incomplete circle or org profiles
    // get redirected to the appropriate setup page.
    // Checks are batched into a single effect with Promise.allSettled
    // to reduce latency vs two sequential API calls.
    // Admin/super_admin users are skipped — they don't need org profiles
    // and have their own admin-only routes.
    // ────────────────────────────────────────────────────────────────
    useEffect(() => {
      // Skip profile checks for admin roles (they manage orgs, not payroll)
      if (user?.role === 'employee' || isSuperAdmin) {
        setCheckingProfile(false)
        return
      }

      // Collect pages to skip per check type
      const isOnCircleProfilePage = currentPath === '/dashboard/payroll-defaults'
      const isOnOrgProfilePage = currentPath.startsWith('/dashboard/company-profile')

      // If already on either setup page, let the user complete that page
      // before checking the other one. Using OR (not AND) — a path can't be
      // both /payroll-defaults and /company-profile/edit at the same time.
      if (isOnCircleProfilePage || isOnOrgProfilePage) {
        setCheckingProfile(false)
        return
      }

      let cancelled = false

      async function checkAll() {
        // Fire both API calls in parallel — Promise.allSettled means one
        // failure doesn't orphan the other result.
        const [circleProfileResult, orgResult] = await Promise.allSettled([
          getCircleProfileApi(),
          getOrganizationProfileApi(),
        ])
        if (cancelled) return

        // ── 1. Check circle profile ──
        if (!isOnCircleProfilePage) {
          let circleProfileIncomplete = true

          if (circleProfileResult.status === 'fulfilled') {
            const p = circleProfileResult.value.profile
            circleProfileIncomplete = !(
              p &&
              p.country_id != null &&
              p.currency &&
              p.currency.trim().length > 0 &&
              p.payment_cycle &&
              p.payment_cycle.trim().length > 0
            )
          }

          if (circleProfileIncomplete) {
            navigate({ to: '/dashboard/payroll-defaults' })
            return // stop — let the payroll defaults flow resolve first
          }
        }

        // ── 2. Check organization profile ──
        if (!isOnOrgProfilePage) {
          let orgProfileIncomplete = true

          if (orgResult.status === 'fulfilled') {
            const org = orgResult.value.organization
            orgProfileIncomplete = !(
              org &&
              ((org.vat_number && org.vat_number.trim().length > 0) ||
                (org.registration_number && org.registration_number.trim().length > 0)) &&
              org.registered_address &&
              org.registered_address.trim().length > 0 &&
              org.verified_status === 'active'
            )
          }

          if (orgProfileIncomplete) {
            navigate({ to: '/dashboard/company-profile/edit' })
            return
          }
        }

        if (!cancelled) setCheckingProfile(false)
      }

      checkAll()
      return () => { cancelled = true }
    }, [currentPath, user?.role, navigate])

    // Refs for the focus-restore dance: capture the element that was focused
    // before the drawer opened (typically the hamburger button) so we can
    // restore focus there on close; focus the X close button on open so
    // keyboard users land somewhere usable inside the new region.
    const previouslyFocusedRef = useRef<HTMLElement | null>(null)
    const closeBtnRef = useRef<HTMLButtonElement>(null)
    const hamburgerRef = useRef<HTMLButtonElement>(null)

    // Auto-close the drawer on every navigation (initial mount is a no-op
    // since the drawer starts closed).
    useEffect(() => {
      setSidebarOpen(false)
    }, [currentPath])

    // ESC closes the drawer; body scroll is locked while it's open so the
    // page underneath doesn't scroll when the user drags on the scrim.
    useEffect(() => {
      if (!sidebarOpen) return
      function handleKey(e: KeyboardEvent) {
        if (e.key === 'Escape') setSidebarOpen(false)
      }
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKey)
      return () => {
        document.body.style.overflow = previousOverflow
        document.removeEventListener('keydown', handleKey)
      }
    }, [sidebarOpen])

    // Focus management: move focus into the drawer on open (close X), and
    // restore it to the previously-focused element on close. The DOM check
    // guards against restoration when the previously-focused element was
    // unmounted by a route change while the drawer was open.
    useEffect(() => {
      if (sidebarOpen) {
        previouslyFocusedRef.current = document.activeElement as HTMLElement | null
        closeBtnRef.current?.focus()
      } else if (
        previouslyFocusedRef.current &&
        document.body.contains(previouslyFocusedRef.current)
      ) {
        previouslyFocusedRef.current.focus()
        previouslyFocusedRef.current = null
      }
    }, [sidebarOpen])

    const closeSidebar = useCallback(() => setSidebarOpen(false), [])

    // Show a brief loading state while checking profile completeness
    if (checkingProfile) {
      return (
        <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading dashboard…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="flex min-h-[calc(100svh-3.5rem)]">
        {/* Mobile drawer — only mounted when open so the closed state
            doesn't ship any off-screen DOM. Backdrop + ESC + every Link tap
            close it. Hidden on md+ (≥768px) where the static desktop aside
            takes over. Same <SidebarContent/> as desktop — no markup
            duplication, so link list + active state + logout stay in sync
            automatically. */}
        {sidebarOpen && (
          <>
            <button
              type="button"
              aria-label="Close menu"
              tabIndex={-1}
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={closeSidebar}
            />
            <aside
              aria-label="Dashboard navigation"
              className="fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-left motion-safe:duration-200 md:hidden"
            >
              <SidebarContent
                currentPath={currentPath}
                isEmployee={isEmployee}
                isSuperAdmin={isSuperAdmin}
                onNavigate={closeSidebar}
                closeBtnRef={closeBtnRef}
              />
            </aside>
          </>
        )}

        {/* Desktop sidebar — visible at md+ (≥768px). Mobile drawer above
            is the <768px variant of the same <SidebarContent/> source. */}
        <aside
          aria-label="Dashboard navigation"
          className="hidden w-60 shrink-0 flex-col border-r border-border md:flex"
        >
          <SidebarContent currentPath={currentPath} isEmployee={isEmployee} isSuperAdmin={isSuperAdmin} />
        </aside>

        <main className="min-w-0 flex-1 overflow-auto p-4 sm:p-6 md:p-8">
          {/* Top header bar with hamburger (mobile-only), page title, and
              notification bell. The hamburger is hidden on md+ where the
              desktop sidebar is always visible. size-11 = 44px (WCAG 2.5.5
              AAA / Apple HIG / Material). */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                ref={hamburgerRef}
                type="button"
                aria-label="Open menu"
                aria-expanded={sidebarOpen}
                onClick={() => setSidebarOpen(true)}
                className="flex size-11 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted md:hidden"
              >
                <MenuIcon className="size-5" />
              </button>
              <span className="text-sm font-semibold">Dashboard</span>
            </div>
            <NotificationBell />
          </div>
          <Outlet />
        </main>
      </div>
    )
  },
})
